/**
 * Interview Loop dialogs reusable from the Recruitment ATS:
 *  - ScheduleRoundDialog: schedule a new interview round for a candidate
 *  - HiringCommitteeDialog: aggregated multi-round view + DeepSeek synthesis + decide
 *
 * Kept in a sibling file so Recruitment.jsx doesn't grow past 1500 lines.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Sparkle, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const REC_LABEL = {
  strong_hire: { label: "Strong Hire", color: "bg-emerald-600 text-white" },
  hire: { label: "Hire", color: "bg-emerald-100 text-emerald-800" },
  no_hire: { label: "No Hire", color: "bg-rose-100 text-rose-800" },
  strong_no_hire: { label: "Strong No Hire", color: "bg-rose-600 text-white" },
};

const fmtDT = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || ""; }
};
const errMsg = (e) => formatApiError(e?.response?.data?.detail) || "Something went wrong";

/* ---------------------- ScheduleRoundDialog ---------------------- */
export function ScheduleRoundDialog({ cand, onClose, onScheduled }) {
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [tplId, setTplId] = useState("");
  const [roundName, setRoundName] = useState("");
  const [roundType, setRoundType] = useState("other");
  const [scheduledAt, setScheduledAt] = useState("");
  const [duration, setDuration] = useState(45);
  const [mode, setMode] = useState("video");
  const [meetingLink, setMeetingLink] = useState("");
  const [location, setLocation] = useState("");
  const [interviewerIds, setInterviewerIds] = useState([]);
  const [interviewerSearch, setInterviewerSearch] = useState("");
  const [agenda, setAgenda] = useState("");
  const [notifyCandidate, setNotifyCandidate] = useState(true);
  const [notifyInterviewers, setNotifyInterviewers] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/interview-loop/scorecard-templates"),
      api.get("/employees?limit=500"),
    ]).then(([t, e]) => {
      setTemplates(t.data);
      const arr = Array.isArray(e.data) ? e.data : (e.data.items || []);
      setEmployees(arr);
      if (t.data.length) {
        const first = t.data[0];
        setTplId(first.id);
        setRoundType(first.round_type);
        setRoundName(`Round 1 — ${first.name}`);
      }
    }).catch(err => toast.error(errMsg(err)))
      .finally(() => setLoading(false));
  }, []);

  const onTplChange = (id) => {
    setTplId(id);
    const t = templates.find(x => x.id === id);
    if (t) {
      setRoundType(t.round_type);
      // Suggest a round name based on the template if user hasn't customised it
      if (!roundName.trim() || roundName.startsWith("Round")) {
        setRoundName(`Round — ${t.name}`);
      }
    }
  };

  const filteredEmployees = useMemo(() => {
    const s = interviewerSearch.toLowerCase().trim();
    if (!s) return employees.slice(0, 50);
    return employees.filter(e =>
      (e.name || "").toLowerCase().includes(s) ||
      (e.email || "").toLowerCase().includes(s) ||
      (e.department || "").toLowerCase().includes(s)
    ).slice(0, 50);
  }, [employees, interviewerSearch]);

  const toggleInterviewer = (id) => setInterviewerIds(ids =>
    ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]
  );
  const interviewerNames = useMemo(
    () => interviewerIds.map(id => employees.find(e => e.id === id)?.name).filter(Boolean),
    [interviewerIds, employees]
  );

  const submit = async () => {
    if (!tplId) { toast.error("Pick a scorecard template"); return; }
    if (!roundName.trim()) { toast.error("Round name required"); return; }
    if (!scheduledAt) { toast.error("Pick date & time"); return; }
    if (!interviewerIds.length) { toast.error("Add at least one interviewer"); return; }
    setSubmitting(true);
    try {
      const isoLike = new Date(scheduledAt).toISOString();
      const payload = {
        candidate_id: cand.id,
        round_name: roundName.trim(),
        round_type: roundType,
        scorecard_template_id: tplId,
        scheduled_at: isoLike,
        duration_min: parseInt(duration, 10) || 45,
        mode,
        location: mode === "in_person" ? (location || null) : null,
        meeting_link: mode === "video" ? (meetingLink || null) : null,
        interviewer_ids: interviewerIds,
        agenda: agenda || null,
        notify_candidate: notifyCandidate,
        notify_interviewers: notifyInterviewers,
      };
      const { data } = await api.post("/interview-loop/schedule", payload);
      toast.success(`Round scheduled. ${notifyCandidate ? "Candidate invited." : ""} ${notifyInterviewers ? "Interviewers notified." : ""}`);
      onScheduled?.(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="schedule-round-dialog">
        <DialogHeader>
          <DialogTitle>Schedule an interview round</DialogTitle>
          <DialogDescription>
            {cand.name} · {cand.email}{cand.requisition_title ? ` · ${cand.requisition_title}` : ""}
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Round name</Label>
                <Input value={roundName} onChange={(e) => setRoundName(e.target.value)} data-testid="sched-round-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Scorecard template</Label>
                <select
                  value={tplId}
                  onChange={(e) => onTplChange(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded text-sm"
                  data-testid="sched-tpl-picker"
                >
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <div className="text-[11px] text-zinc-500 mt-1">{templates.find(t => t.id === tplId)?.description}</div>
              </div>
              <div>
                <Label className="text-xs">Date & time</Label>
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  data-testid="sched-when"
                />
              </div>
              <div>
                <Label className="text-xs">Duration (min)</Label>
                <Input type="number" min={15} max={240} value={duration}
                  onChange={(e) => setDuration(e.target.value)} data-testid="sched-duration" />
              </div>
              <div>
                <Label className="text-xs">Mode</Label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-zinc-300 rounded text-sm"
                  data-testid="sched-mode"
                >
                  <option value="video">Video</option>
                  <option value="in_person">In person</option>
                  <option value="phone">Phone</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">{mode === "video" ? "Meeting link" : (mode === "in_person" ? "Location" : "Phone number")}</Label>
                {mode === "video" ? (
                  <Input value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} placeholder="https://meet.google.com/abc" data-testid="sched-link" />
                ) : (
                  <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={mode === "in_person" ? "Office address or room" : "+91 …"} data-testid="sched-location" />
                )}
              </div>
            </div>

            <div className="border border-zinc-200 rounded p-3">
              <Label className="text-sm font-medium">Interviewers ({interviewerIds.length})</Label>
              <Input
                value={interviewerSearch}
                onChange={(e) => setInterviewerSearch(e.target.value)}
                placeholder="Search by name, email, department…"
                className="mt-2"
                data-testid="sched-emp-search"
              />
              {interviewerIds.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {interviewerIds.map(id => {
                    const e = employees.find(x => x.id === id);
                    return (
                      <button
                        key={id}
                        onClick={() => toggleInterviewer(id)}
                        className="text-[11px] px-2 py-0.5 bg-zinc-900 text-white rounded hover:bg-zinc-700"
                        title="Remove"
                      >
                        {e?.name || id} ×
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="max-h-48 overflow-y-auto mt-2 border-t pt-2 space-y-0.5">
                {filteredEmployees.map(e => (
                  <button
                    key={e.id}
                    onClick={() => toggleInterviewer(e.id)}
                    className={`w-full text-left px-2 py-1 text-xs rounded hover:bg-zinc-50 flex items-center justify-between ${interviewerIds.includes(e.id) ? "bg-emerald-50" : ""}`}
                    data-testid={`sched-emp-${e.id}`}
                  >
                    <span>{e.name} <span className="text-zinc-400">· {e.email || "—"}</span></span>
                    {interviewerIds.includes(e.id) && <span className="text-emerald-600">✓</span>}
                  </button>
                ))}
                {filteredEmployees.length === 0 && (
                  <div className="text-xs text-zinc-500 px-2 py-3">No employees match.</div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs">Agenda / notes for the interviewer (optional)</Label>
              <Textarea rows={2} value={agenda} onChange={(e) => setAgenda(e.target.value)} placeholder="Focus on system design, ask about Project X, etc." data-testid="sched-agenda"/>
            </div>

            <div className="flex items-center justify-between border-t pt-3 text-sm">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={notifyCandidate} onChange={(e) => setNotifyCandidate(e.target.checked)} data-testid="sched-notify-cand"/>
                Email candidate with calendar invite
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={notifyInterviewers} onChange={(e) => setNotifyInterviewers(e.target.checked)} data-testid="sched-notify-iv"/>
                Email interviewers with calendar invite
              </label>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || loading} data-testid="sched-submit">
            {submitting ? "Scheduling…" : "Schedule round"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------------- HiringCommitteeDialog ---------------------- */
export function HiringCommitteeDialog({ cand, onClose, onDecided }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [deciding, setDeciding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/interview-loop/candidates/${cand.id}/hiring-committee`);
      setData(data);
      // Surface cached synthesis if present on candidate
      if (cand.hiring_committee_synthesis) setAi(cand.hiring_committee_synthesis);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const runAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/interview-loop/candidates/${cand.id}/hiring-committee/ai-synthesis`);
      setAi(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setAiBusy(false); }
  };

  const decide = async (decision) => {
    const reason = decision === "reject"
      ? (window.prompt("Reason for rejection (will be saved):") || "").trim() || null
      : null;
    setDeciding(true);
    try {
      const { data } = await api.post(`/interview-loop/candidates/${cand.id}/decide`, { decision, reason });
      toast.success(`Committee decision saved: ${decision}`);
      onDecided?.(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDeciding(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="hiring-committee-dialog">
        <DialogHeader>
          <DialogTitle>Hiring committee — {cand.name}</DialogTitle>
          <DialogDescription>
            {cand.requisition_title || "—"} · {data?.aggregate?.total_scorecards || 0} scorecards across {data?.rounds?.length || 0} rounds
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : (
          <div className="space-y-5">
            {/* Aggregate */}
            {data?.aggregate && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="committee-aggregate">
                {Object.entries(data.aggregate.recommendation_counts || {}).map(([k, v]) => {
                  const m = REC_LABEL[k];
                  return (
                    <div key={k} className={`p-3 rounded border ${m?.color || "bg-zinc-100 text-zinc-700"} border-transparent`}>
                      <div className="text-xs uppercase tracking-wide opacity-80">{m?.label || k}</div>
                      <div className="text-2xl font-semibold mt-1">{v}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* DeepSeek synthesis card */}
            <div className="border rounded-lg p-4 bg-violet-50 border-violet-200" data-testid="committee-synthesis">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <Sparkle size={16} className="text-violet-700"/> AI synthesis (DeepSeek)
                </div>
                <Button size="sm" variant="outline" onClick={runAi} disabled={aiBusy} data-testid="committee-synth-run">
                  {aiBusy ? "Synthesizing…" : (ai ? "Regenerate" : "Run synthesis")}
                </Button>
              </div>
              {ai ? (
                <div className="space-y-2 text-sm">
                  {ai.recommendation && (
                    <div>
                      <Badge className="bg-violet-700 text-white uppercase">{ai.recommendation.replace(/_/g, " ")}</Badge>
                    </div>
                  )}
                  {ai.summary && <p className="text-zinc-700">{ai.summary}</p>}
                  <div className="grid md:grid-cols-2 gap-3 mt-2">
                    <div>
                      <div className="text-xs font-medium text-emerald-700 mb-1">Strengths</div>
                      <ul className="text-xs text-zinc-700 list-disc list-inside space-y-0.5">
                        {(ai.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-rose-700 mb-1">Concerns</div>
                      <ul className="text-xs text-zinc-700 list-disc list-inside space-y-0.5">
                        {(ai.concerns || []).map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-zinc-500">
                  Click "Run synthesis" to let DeepSeek summarise the panel's view.
                </div>
              )}
            </div>

            {/* Rounds */}
            <div className="space-y-3">
              {(data?.rounds || []).map((r, idx) => (
                <div key={r.id} className="border rounded p-3" data-testid={`committee-round-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{r.round_name}</div>
                      <div className="text-xs text-zinc-500">{fmtDT(r.scheduled_at)} · {(r.mode || "").replace("_", " ")} · {r.duration_min} min</div>
                    </div>
                    <Badge className="text-[10px]">{r.scorecards_submitted_count}/{r.scorecards_required_count} scorecards</Badge>
                  </div>
                  {(r.scorecards || []).length > 0 && (
                    <div className="mt-2 space-y-1">
                      {r.scorecards.map(sc => {
                        const m = REC_LABEL[sc.overall_recommendation];
                        return (
                          <div key={sc.id} className="text-xs border-l-2 border-zinc-200 pl-2 py-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{sc.interviewer_name}</span>
                              {m && <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.color}`}>{m.label}</span>}
                            </div>
                            {sc.notes && <div className="text-zinc-600 mt-0.5">{sc.notes}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              {(!data?.rounds || data.rounds.length === 0) && (
                <div className="text-sm text-zinc-500 text-center py-8 border border-dashed rounded">
                  No rounds scheduled yet for this candidate.
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter className="flex-row justify-between">
          <div className="space-x-2">
            <Button variant="default" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide("advance")} disabled={deciding} data-testid="commit-advance">Advance to offer</Button>
            <Button variant="outline" onClick={() => decide("hold")} disabled={deciding} data-testid="commit-hold">Hold</Button>
            <Button variant="outline" className="text-rose-700 border-rose-300" onClick={() => decide("reject")} disabled={deciding} data-testid="commit-reject">Reject</Button>
          </div>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
